export const CURRENT_ACHIEVEMENT_RULE_VERSION = 1;
/** Staged until the data adapters, durable processing, and rollout checks are complete. */
export const NEXT_ACHIEVEMENT_RULE_VERSION = 2;

export type AchievementBadgeKey =
  | "first-pick"
  | "full-slate"
  | "sharp-shooter"
  | "perfect-card"
  | "weekly-winner"
  | "hot-hand"
  | "25-correct"
  | "underdog-whisperer"
  | "giant-killer"
  | "long-shot-legend"
  | "road-warrior"
  | "home-field-oracle"
  | "hook-hunter"
  | "cover-artist"
  | "overdrive"
  | "understudy"
  | "prop-shop"
  | "four-tool-captain"
  | "same-game-sweep"
  | "primetime-prophet"
  | "london-calling"
  | "division-decoder"
  | "four-week-furnace"
  | "hat-trick"
  | "bookend-bandit"
  | "standings-sniper"
  | "early-bird-special"
  | "iron-slate"
  | "no-blank-sundays"
  | "bounce-back-artist"
  | "century-club"
  | "the-locksmith"
  | "steady-eddie"
  | "double-digit-champ"
  | "dog-money"
  | "small-slate-specialist"
  | "never-say-die";

export type AchievementIcon = "flag" | "clipboard" | "target" | "bullseye" | "trophy" | "flame" | "check";

export interface AchievementDefinition {
  key: AchievementBadgeKey;
  title: string;
  description: string;
  rule: string;
  icon: AchievementIcon;
  repeatable: boolean;
  displayOrder: number;
  ruleVersion: number;
}

/** Immutable definitions are indexed by rule version so in-progress seasons retain their rules. */
import { EXPANDED_ACHIEVEMENT_DEFINITIONS } from "./achievementCatalogV2";

export const ACHIEVEMENT_CATALOGS: Record<number, readonly AchievementDefinition[]> = {
  1: [
    { key: "first-pick", title: "First Pick", description: "Your first pick of the season.", rule: "Save your first pick before it locks. Random picks count too.", icon: "flag", repeatable: false, displayOrder: 1, ruleVersion: 1 },
    { key: "full-slate", title: "Full Slate", description: "You filled out the card.", rule: "Fill out your entire weekly card, including the tiebreaker, before picks lock.", icon: "clipboard", repeatable: false, displayOrder: 2, ruleVersion: 1 },
    { key: "sharp-shooter", title: "Sharp Shooter", description: "You picked accurately across the slate.", rule: "Get at least 70% of your picks right in one week.", icon: "target", repeatable: false, displayOrder: 3, ruleVersion: 1 },
    { key: "perfect-card", title: "Perfect Card", description: "Every pick went your way.", rule: "Fill out your weekly card and get every pick right. Pushes don't count against you.", icon: "bullseye", repeatable: false, displayOrder: 4, ruleVersion: 1 },
    { key: "weekly-winner", title: "Weekly Winner", description: "You finished with the top weekly score.", rule: "Finish the week with the highest score above zero. Ties share the award.", icon: "trophy", repeatable: true, displayOrder: 5, ruleVersion: 1 },
    { key: "hot-hand", title: "Hot Hand", description: "Three strong weeks in a row.", rule: "Get at least 60% of your picks right for three weeks in a row.", icon: "flame", repeatable: false, displayOrder: 6, ruleVersion: 1 },
    { key: "25-correct", title: "25 Correct", description: "Twenty-five picks went your way.", rule: "Get 25 picks right during the season. Random picks count; tiebreakers don't.", icon: "check", repeatable: false, displayOrder: 7, ruleVersion: 1 },
  ],
};

ACHIEVEMENT_CATALOGS[2] = [
  ...ACHIEVEMENT_CATALOGS[1]!.map((definition) => ({ ...definition, ruleVersion: 2 })),
  ...EXPANDED_ACHIEVEMENT_DEFINITIONS,
];

export const getAchievementCatalog = (ruleVersion: number): readonly AchievementDefinition[] =>
  ACHIEVEMENT_CATALOGS[ruleVersion] ?? [];

/** Curated difficulty order shared by personal previews and league-mate highlights. */
const highlightOrder: readonly AchievementBadgeKey[] = [
  "never-say-die", "london-calling", "double-digit-champ", "bookend-bandit",
  "long-shot-legend", "hat-trick", "the-locksmith", "iron-slate", "four-week-furnace",
  "giant-killer", "perfect-card", "standings-sniper", "dog-money", "steady-eddie",
  "no-blank-sundays", "century-club", "early-bird-special", "same-game-sweep",
  "four-tool-captain", "underdog-whisperer", "home-field-oracle", "road-warrior",
  "cover-artist", "overdrive", "understudy", "hook-hunter", "primetime-prophet",
  "division-decoder", "bounce-back-artist", "small-slate-specialist", "prop-shop",
  "hot-hand", "weekly-winner", "sharp-shooter", "25-correct", "full-slate", "first-pick",
];

export const getAchievementHighlightPriority = (key: AchievementBadgeKey): number => {
  const priority = highlightOrder.indexOf(key);
  return priority === -1 ? highlightOrder.length : priority;
};

export interface AchievementEvidence {
  weekLabel?: string;
  correctCount?: number;
  gradedCount?: number;
  eligiblePickCount?: number;
  weeklyPoints?: number;
  rank?: number;
  qualifyingWeeks?: string[];
  count?: number;
  unit?: string;
  accuracyPercent?: number;
  gameId?: string;
}

export interface EarnedAchievement {
  id: string;
  badgeKey: AchievementBadgeKey;
  occurrenceKey: string;
  leagueWeekId: string | null;
  awardedAt: string | null;
  isBackfilled: boolean;
  ruleVersion: number;
  evidence: AchievementEvidence;
}

export interface AchievementProgress {
  badgeKey: AchievementBadgeKey;
  current: number;
  target: number;
  unit: string;
  weekLabel?: string;
}

/** Server-owned pick-time facts. Null means the historical value is unknown. */
export interface AchievementPickSnapshot {
  version: 1;
  selectedOdds: number | null;
  homeSpread: number | null;
}

export interface AchievementGameContext {
  primetime?: boolean | null;
  international?: boolean | null;
  divisionMatchup?: boolean | null;
  source: "provider" | "manual";
}
